import { useEffect, useRef, useState } from "react";

// models
import { Employee2 } from "../../../../../../model/Employee";

// Utils
import { getThemeStyles, Theme } from '../../../../../../util/style';

// lucide icons
import { GraduationCap, Landmark, NotepadText, UserRoundX, X } from 'lucide-react';

type DetailTab = "umum" | "rekening" | "pendidikan" | "resign";

export default function EmployeeModalDetail({
    theme = "light",
    isOpen,
    onClose,
    employee,
}: {
    theme?: Theme;
    isOpen: boolean;
    onClose: () => void;
    employee?: Employee2 | null;
}) {
    const s = getThemeStyles(theme);
    const modalRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<DetailTab>("umum");

    const rekening = employee?.rekening ?? {};
    const hasRekening = Object.keys(rekening).length > 0;

    const pendidikan = employee?.pendidikan ?? {};
    const hasPendidikan = Object.keys(pendidikan).length > 0;

    const resign = employee?.resign ?? {};
    const hasResign = Object.keys(resign).length > 0;

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (!modalRef.current?.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen && !employee) return null;

    return (
        <div className="modal-overlay">
            <div className={`modal ${s.card}`} ref={modalRef}>
                <div className="modal-header">
                    <span className={`modal-title ${s.text}`}>Detail Karyawan</span>
                    <button className="modal-close" onClick={() => { onClose(); setActiveTab("umum") }}><X /></button>
                </div>
                <div className="modal-tabs">
                    <button className={`modal-tab ${activeTab === "umum" ? "active" : ""}`} onClick={() => setActiveTab("umum")}>
                        <NotepadText />
                        Informasi Umum
                    </button>
                    <button className={`modal-tab ${activeTab === "rekening" ? "active" : ""}`} onClick={() => setActiveTab("rekening")}>
                        <Landmark />
                        Informasi Rekening
                    </button>
                    <button className={`modal-tab ${activeTab === "pendidikan" ? "active" : ""}`} onClick={() => setActiveTab("pendidikan")}>
                        <GraduationCap />
                        Riwayat Pendidikan
                    </button>
                    <button className={`modal-tab ${activeTab === "resign" ? "active" : ""}`} onClick={() => setActiveTab("resign")}>
                        <UserRoundX />
                        Informasi Resign
                    </button>
                </div>
                <div className="modal-body">
                    {activeTab === "umum" && (
                        <>
                            <div className="detail-foto" onClick={() => console.log(employee)}>
                                {employee?.foto_karyawan ?
                                    <img src={process.env.REACT_APP_API_URL + "/storage/route/karyawan/" + encodeURIComponent(String(employee?.nama)) + "/" + employee?.foto_karyawan} alt={employee?.nama} /> 
                                :
                                    (employee?.nama ?? '').split(' ').map(n => n[0] || '').join('').slice(0, 2)
                                }
                            </div>

                            <div className="detail-section">
                                <div className="detail-section-title">Data Pribadi</div>
                                <div className="detail-grid">
                                    {[
                                        ["NIK (Nomor Induk Kependudukan)", employee?.nik],
                                        ["Nama Lengkap", employee?.nama],
                                        ["Jenis Kelamin", employee?.jk],
                                        ["Tanggal Lahir", `${employee?.tgl_lahir_formatted} (${employee?.usia} tahun)`],
                                        ["Agama", employee?.agama],

                                        ["Email", employee?.email],
                                        ["Nomor HP Pribadi", employee?.no_hp_pribadi || "-"],
                                        ["Nomor HP Perusahaan", employee?.no_hp_perusahaan || "-"],
                                        
                                        ["Alamat Sesuai KTP", employee?.alamat_rumah],
                                        ["Alamat Domisili", employee?.alamat_sekarang],
                                        ["Kabupaten/Kota", employee?.kota],
                                        ["Kode Pos", employee?.kode_pos],
                                        ["NPWP (Nomor Pokok Wajib Pajak)", employee?.npwp || "-"]
                                    ].map(([k, v], idx) => (
                                        <div key={idx} className="detail-item">
                                            <div className="detail-key">{k}</div>
                                            <div className="detail-val">{v}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="detail-section">
                                <div className="detail-section-title">Data Pekerjaan</div>
                                <div className="detail-grid">
                                {[
                                    ["Jabatan", employee?.jabatan || "-"],
                                    ["Divisi", employee?.divisi || "-"],
                                    ["Status Kepegawaian", employee?.status_pegawai || "-"],
                                    ["Lokasi Kerja", employee?.lokasi_kerja || "-"],
                                    ["Tanggal Bergabung", employee?.tgl_bergabung_formatted || "-"],
                                    ["Masa Kerja", employee?.lama_kerja || "-"]
                                ].map(([k, v], idx) => (
                                    <div key={idx} className="detail-item">
                                        <div className="detail-key">{k}</div>
                                        <div className="detail-val">{v}</div>
                                    </div>
                                ))}
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === "rekening" && (
                        <>
                            <p className="text-sm text-[var(--text-muted)] mb-3">
                                Informasi rekening bank yang terdaftar untuk karyawan.
                            </p>

                            {hasRekening ? (
                                <div className="detail-grid">
                                    {Object.entries(rekening).map(([bank, nomor]) => (
                                        <div key={bank} className="detail-item">
                                            <div className="detail-key">{bank}</div>
                                            <div className="detail-val">{nomor}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="json-view-block">
                                    <span className="json-null">
                                        Belum ada data rekening bank yang terdaftar.
                                    </span>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "pendidikan" && (
                        <>
                            <p className="text-sm text-[var(--text-muted)] mb-3">
                                Informasi riwayat pendidikan karyawan.
                            </p>

                            {hasPendidikan ? (
                                <div className="detail-grid">
                                    {Object.entries(pendidikan).map(([key, value]) => (
                                        <div key={key} className="detail-item">
                                            <div className="detail-key">
                                                {key
                                                    .replace(/_/g, " ")
                                                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                                            </div>
                                            <div className="detail-val">
                                                {String(value ?? "-")}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="json-view-block">
                                    <span className="json-null">
                                        Belum ada data riwayat pendidikan yang terdaftar.
                                    </span>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "resign" && (
                        <>
                            <p className="text-sm text-[var(--text-muted)] mb-3">
                                Informasi status resign karyawan.
                            </p>

                            {hasResign ? (
                                <div className="detail-grid">
                                    {Object.entries(resign).map(([key, value]) => (
                                        <div key={key} className="detail-item">
                                            <div className="detail-key">
                                                {key
                                                    .replace(/_/g, " ")
                                                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                                            </div>
                                            <div className="detail-val">
                                                {String(value ?? "-")}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="json-view-block">
                                    <span className="json-null">
                                        Karyawan masih aktif bekerja. Data resign belum tersedia.
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}